import React, { Fragment, useMemo, useState } from 'react';

import { Input } from '../../Form/Input/Input';
import { CenteredSpinner } from '../../CenteredSpinner/CenteredSpinner';
import { PresetItem } from '../PresetItem/PresetItem';
import { useProfiles } from '../../../hooks/useProfiles';
import { getFirstUnselectedResult } from '../helpers';

import type { EnrichedTag } from '../../../types';
import type { ChangeEvent, FC, KeyboardEvent } from 'react';

import styles from './ProfilesSearch.module.scss';

type Props = {
  selectedProfiles: EnrichedTag[];
  onAddClick: (profile: EnrichedTag) => void;
  onRemoveClick: (profile: EnrichedTag) => void;
  label?: string;
};

export const ProfilesSearch: FC<Props> = ({
  selectedProfiles = [],
  onAddClick,
  onRemoveClick,
  label = 'Search by title or ID',
}) => {
  const { data: profiles = [] } = useProfiles();
  const [filter, setFilter] = useState('');

  const filteredProfiles = useMemo(() => {
    if (filter === '') {
      return profiles;
    }

    const lowerCaseFilter = filter.toLowerCase();

    return profiles.filter(
      ({ title, id }) =>
        title.toLowerCase().includes(lowerCaseFilter) ||
        id.toLowerCase().includes(lowerCaseFilter)
    );
  }, [profiles, filter]);

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') {
      return;
    }

    const selectedIds = selectedProfiles.map(({ id }) => id);
    const nextResult = getFirstUnselectedResult(filteredProfiles, selectedIds);

    if (nextResult) {
      onAddClick(nextResult);
      setFilter('');
    }
  }

  return (
    <Fragment>
      <Input
        label={label}
        name="profileTitle"
        value={filter}
        disabled={profiles.length === 0}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setFilter(event.target.value);
        }}
        onKeyDown={onInputKeyDown}
        data-testid="preset-search-input"
      />
      <small className={styles.hint}>Press Enter to add the top result</small>
      {profiles.length === 0 && <CenteredSpinner />}
      <ul className={styles.profilesList} data-testid="preset-results">
        {filteredProfiles.map((profile) => {
          const isSelected = selectedProfiles
            .map((presetItem) => presetItem.id)
            .includes(profile.id);

          return (
            <li key={profile.id}>
              <PresetItem
                item={profile}
                dataType={'profiles'}
                isSelected={isSelected}
                onClick={() =>
                  isSelected ? onRemoveClick(profile) : onAddClick(profile)
                }
              />
            </li>
          );
        })}
      </ul>
    </Fragment>
  );
};
